/**
 * Hydrate sparse / incomplete v3 config color data on import.
 * Computed display fields (hex, oklch, p3) are optional — the color engine recalculates them.
 */

export const DEFAULT_STATIC_COLOR_VALUE = 'oklch(0.5 65% 275)';

export function generateImportId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Unique name with Color / Color 2 / Color 3 numbering (skips "… 1").
 * @param {string} baseName
 * @param {Set<string>} usedNames
 */
export function getUniqueName(baseName, usedNames) {
  const base = typeof baseName === 'string' && baseName.trim() ? baseName.trim() : 'Color';
  if (!usedNames.has(base)) return base;
  let n = 2;
  while (usedNames.has(`${base} ${n}`)) n++;
  return `${base} ${n}`;
}

/**
 * Friendlier message for JSON.parse failures when importing configs.
 * @param {unknown} error
 * @param {string} [text]
 */
export function formatJsonImportError(error, text = '') {
  const raw = error && typeof error.message === 'string' ? error.message : String(error || 'Invalid JSON');
  const lineMatch = raw.match(/line (\d+)/i);
  const line = lineMatch ? Number(lineMatch[1]) : null;

  const hint =
    'Colors in an array must be separated by commas, and every field needs a value (or omit the field entirely). ' +
    'Sparse contrast example: {"mode":"contrast","backgroundColorId":"<bg-id>","foregroundColorId":"<fg-id>","contrastLevel":15}';

  if (line != null && Number.isFinite(line)) {
    return `Invalid JSON near line ${line}: ${hint}`;
  }
  return `Invalid JSON: ${raw}. ${hint}`;
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function resolveStaticColorValue(color) {
  if (isNonEmptyString(color.colorValue)) return color.colorValue.trim();
  if (isNonEmptyString(color.hexValue)) return color.hexValue.trim();
  if (isNonEmptyString(color.oklchValue)) return color.oklchValue.trim();
  return DEFAULT_STATIC_COLOR_VALUE;
}

/**
 * @param {Array} folders
 * @param {{ generateId?: () => string }} [opts]
 */
export function hydrateFolders(folders, opts = {}) {
  const generateId = opts.generateId || generateImportId;
  const list = Array.isArray(folders) ? folders : [];
  const seenIds = new Set();
  const usedNamesByParent = new Map(); // parentKey -> Set of names

  const result = list.map((raw) => {
    const folder = raw && typeof raw === 'object' ? { ...raw } : {};
    let id = isNonEmptyString(folder.id) ? String(folder.id) : '';
    if (!id || seenIds.has(id)) {
      do {
        id = generateId();
      } while (seenIds.has(id));
    }
    seenIds.add(id);

    const parentId =
      folder.parentId === undefined || folder.parentId === null || folder.parentId === ''
        ? null
        : String(folder.parentId);
    const parentKey = parentId == null ? '__root__' : parentId;
    if (!usedNamesByParent.has(parentKey)) usedNamesByParent.set(parentKey, new Set());
    const usedNames = usedNamesByParent.get(parentKey);

    let name;
    if (!isNonEmptyString(folder.name)) {
      name = getUniqueName('Folder', usedNames);
    } else {
      const provided = String(folder.name).trim();
      name = usedNames.has(provided) ? getUniqueName(provided, usedNames) : provided;
    }
    usedNames.add(name);

    return {
      ...folder,
      id,
      name,
      parentId
    };
  });

  // Drop invalid parent links (unknown parent → root)
  const validIds = new Set(result.map((f) => f.id));
  return result.map((f) => ({
    ...f,
    parentId: f.parentId && validIds.has(f.parentId) ? f.parentId : null
  }));
}

/**
 * Assign stable unique ids (global) and unique names (per folder); fix folderId / mode / mode fields.
 * Reference resolution happens in a second pass once all ids exist.
 *
 * @param {Array} colors
 * @param {{
 *   folderIds: Set<string>,
 *   globalIds: Set<string>,
 *   generateId?: () => string,
 *   contrastAlgorithm?: 'apca' | 'wcag'
 * }} opts
 */
export function hydrateColorList(colors, opts) {
  const {
    folderIds,
    globalIds,
    generateId = generateImportId,
    contrastAlgorithm = 'apca'
  } = opts;
  const list = Array.isArray(colors) ? colors : [];
  const usedNamesByFolder = new Map(); // folderKey -> Set of names
  const defaultContrastLevel = contrastAlgorithm === 'wcag' ? 4.5 : 60;

  return list.map((raw) => {
    const color = raw && typeof raw === 'object' ? { ...raw } : {};

    let id = isNonEmptyString(color.id) ? String(color.id) : '';
    if (!id || globalIds.has(id)) {
      do {
        id = generateId();
      } while (globalIds.has(id));
    }
    globalIds.add(id);

    let folderId =
      color.folderId === undefined || color.folderId === null || color.folderId === ''
        ? null
        : String(color.folderId);
    if (folderId && !folderIds.has(folderId)) {
      folderId = null;
    }

    const folderKey = folderId == null ? '__root__' : folderId;
    if (!usedNamesByFolder.has(folderKey)) usedNamesByFolder.set(folderKey, new Set());
    const usedNames = usedNamesByFolder.get(folderKey);

    let name;
    if (!isNonEmptyString(color.name)) {
      name = getUniqueName('Color', usedNames);
    } else {
      const provided = String(color.name).trim();
      name = usedNames.has(provided) ? getUniqueName(provided, usedNames) : provided;
    }
    usedNames.add(name);

    const modeRaw = isNonEmptyString(color.mode) ? String(color.mode).trim().toLowerCase() : 'static';
    const mode =
      modeRaw === 'linked' || modeRaw === 'contrast' || modeRaw === 'static' ? modeRaw : 'static';

    const next = {
      ...color,
      id,
      name,
      mode,
      folderId,
      type: color.type || 'color'
    };

    if (mode === 'static') {
      next.colorValue = resolveStaticColorValue(color);
    } else if (mode === 'linked') {
      next.lExpression = color.lExpression == null ? '' : String(color.lExpression);
      next.cExpression = color.cExpression == null ? '' : String(color.cExpression);
      next.hExpression = color.hExpression == null ? '' : String(color.hExpression);
      next.aExpression = color.aExpression == null ? '' : String(color.aExpression);
      if (color.referenceColorId != null && color.referenceColorId !== '') {
        next.referenceColorId = String(color.referenceColorId);
      } else {
        next.referenceColorId = '';
      }
    } else if (mode === 'contrast') {
      next.useAlpha = color.useAlpha === true;
      if (color.contrastLevel === undefined || color.contrastLevel === null || color.contrastLevel === '') {
        next.contrastLevel = defaultContrastLevel;
      } else {
        const n = Number(color.contrastLevel);
        next.contrastLevel = Number.isFinite(n) ? n : defaultContrastLevel;
      }
      if (color.backgroundColorId != null && color.backgroundColorId !== '') {
        next.backgroundColorId = String(color.backgroundColorId);
      } else {
        next.backgroundColorId = '';
      }
      if (color.foregroundColorId != null && color.foregroundColorId !== '') {
        next.foregroundColorId = String(color.foregroundColorId);
      } else {
        next.foregroundColorId = '';
      }
    }

    return next;
  });
}

/**
 * First / second available reference ids in a pool, excluding self.
 * @param {Array<{id: string}>} pool
 * @param {string} excludeId
 * @returns {{ first: string|null, second: string|null }}
 */
function pickReferenceIds(pool, excludeId) {
  const others = (pool || []).filter((c) => c && c.id && c.id !== excludeId);
  const first = others[0]?.id || null;
  const second = others.length > 1 ? others[1].id : first;
  return { first, second };
}

function idExistsInPool(pool, id) {
  if (!id) return false;
  return (pool || []).some((c) => c && c.id === id);
}

/**
 * Fill missing / unknown linked & contrast references.
 * @returns {string[]} warnings
 */
export function resolveColorReferences(primitives, tokensByTheme, warnings = []) {
  const prims = Array.isArray(primitives) ? primitives : [];

  const fixList = (colors, refPool, listLabel) => {
    return (colors || []).map((color) => {
      if (!color) return color;
      if (color.mode === 'linked') {
        const { first } = pickReferenceIds(refPool, color.id);
        let referenceColorId = color.referenceColorId || '';
        if (!idExistsInPool(refPool, referenceColorId)) {
          if (first) {
            referenceColorId = first;
          } else {
            warnings.push(
              `${listLabel}: linked color "${color.name}" has no available reference color`
            );
            referenceColorId = '';
          }
        }
        return { ...color, referenceColorId };
      }
      if (color.mode === 'contrast') {
        const { first, second } = pickReferenceIds(refPool, color.id);
        let backgroundColorId = color.backgroundColorId || '';
        let foregroundColorId = color.foregroundColorId || '';
        if (!idExistsInPool(refPool, backgroundColorId)) {
          if (first) {
            backgroundColorId = first;
          } else {
            warnings.push(
              `${listLabel}: contrast color "${color.name}" has no available background color`
            );
            backgroundColorId = '';
          }
        }
        if (!idExistsInPool(refPool, foregroundColorId)) {
          if (second || first) {
            // Prefer a different color than bg when possible (CardColor behavior)
            foregroundColorId =
              second && second !== backgroundColorId
                ? second
                : first && first !== backgroundColorId
                  ? first
                  : second || first || '';
            if (!foregroundColorId && first) foregroundColorId = first;
          } else {
            warnings.push(
              `${listLabel}: contrast color "${color.name}" has no available foreground color`
            );
            foregroundColorId = '';
          }
        }
        return { ...color, backgroundColorId, foregroundColorId };
      }
      return color;
    });
  };

  const nextPrimitives = fixList(prims, prims, 'Primitives');

  const nextTokens = {};
  const tokens = tokensByTheme && typeof tokensByTheme === 'object' ? tokensByTheme : {};
  for (const themeId of Object.keys(tokens)) {
    const themeTokens = Array.isArray(tokens[themeId]) ? tokens[themeId] : [];
    const pool = [...nextPrimitives, ...themeTokens];
    nextTokens[themeId] = fixList(themeTokens, pool, `Theme ${themeId}`);
  }

  return { primitives: nextPrimitives, tokens: nextTokens, warnings };
}

/**
 * Hydrate legacy-shaped import data (after collections → legacy conversion).
 *
 * @param {object} data
 * @param {{ contrastAlgorithm?: 'apca' | 'wcag', generateId?: () => string }} [options]
 * @returns {{ data: object, warnings: string[] }}
 */
export function hydrateImportedData(data, options = {}) {
  const contrastAlgorithm = options.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca';
  const generateId = options.generateId || generateImportId;
  const warnings = [];
  const globalIds = new Set();

  const next = { ...data };

  next.primitiveFolders = hydrateFolders(next.primitiveFolders || [], { generateId });
  const primitiveFolderIds = new Set(next.primitiveFolders.map((f) => f.id));

  next.primitives = hydrateColorList(next.primitives || [], {
    folderIds: primitiveFolderIds,
    globalIds,
    generateId,
    contrastAlgorithm
  });

  const colorThemes = Array.isArray(next.colorThemes) ? next.colorThemes : [];
  const tokensIn = next.tokens && typeof next.tokens === 'object' ? next.tokens : {};
  const foldersIn = next.tokenFolders && typeof next.tokenFolders === 'object' ? next.tokenFolders : {};
  const tokensOut = {};
  const foldersOut = {};

  for (const theme of colorThemes) {
    const themeId = theme.id;
    foldersOut[themeId] = hydrateFolders(foldersIn[themeId] || [], { generateId });
    const folderIds = new Set(foldersOut[themeId].map((f) => f.id));
    tokensOut[themeId] = hydrateColorList(tokensIn[themeId] || [], {
      folderIds,
      globalIds,
      generateId,
      contrastAlgorithm
    });
  }

  // Themes may exist as keys without being in colorThemes (defensive)
  for (const themeId of Object.keys(tokensIn)) {
    if (tokensOut[themeId]) continue;
    foldersOut[themeId] = hydrateFolders(foldersIn[themeId] || [], { generateId });
    const folderIds = new Set(foldersOut[themeId].map((f) => f.id));
    tokensOut[themeId] = hydrateColorList(tokensIn[themeId] || [], {
      folderIds,
      globalIds,
      generateId,
      contrastAlgorithm
    });
  }

  next.tokenFolders = foldersOut;
  next.tokens = tokensOut;

  const resolved = resolveColorReferences(next.primitives, next.tokens, warnings);
  next.primitives = resolved.primitives;
  next.tokens = resolved.tokens;

  return { data: next, warnings };
}
